import { useEffect, useState } from 'react';
import {
    HolterProcedureType,
    TcohFollowingSteps,
} from '@frontend/components/pages/customControls/TCOH/v2/TcohFollowingSteps';
import type { IPatient } from '../../../../../../common/interfaces/patient';
import type { IAddress } from '../../../../../../common/utils/getFormattedAddress';
import type { TcohRequestFinalValidated } from '../../../../../../common/validation/customControls/cardiology/holterConsult/cardiology';
import type { ICustomControlRequestReadProps } from '../../interfaces/customControlRequestReadProps';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { Gender } from '../../../../../../common/enums/gender';
import { calculateBmi } from '../../../../../../common/utils/bmi';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { getJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { RequestReadButtons } from '../../../../elements/carePathButtons/RequestReadButtons';
import { FormDivider, FormParagraph, HeaderDivider, UserTextParagraph } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { PatientContactRead } from '../../../../elements/PatientContactRead';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { HideRequestedExaminationInTaskListAndHomeCheckbox } from '../../../requests/HideRequestedExaminationInTaskListAndHomeCheckbox';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { AdviesBijAfwijkendeBeoordelingRead } from './AdviesBijAfwijkendeBeoordelingRead';
import { IndicatieRead } from './IndicatieRead';
import { MedicatieRead } from './MedicatieRead';
import { TcohV2RequestSummary } from './TcohV2RequestSummary';

export function TcohV2RequestRead(props: ICustomControlRequestReadProps) {
    const [patient, setPatient] = useState<IPatient | null>(null);

    useEffect(() => {
        loadPatient();
    }, []);

    return (
        <ProcessContainer>
            <ProcessItemHeader
                date={props.process.dateSent || (props.request && props.request.dateSent)}
                isExaminationRequest
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                organizationName={props.process.requesterOrganization?.name}
                practitionerName={
                    props.process.requesterPractitioner
                        ? getDisplayName(props.process.requesterPractitioner)
                        : undefined
                }
                process={props.process}
                title={getServiceLabel(props.process.code, 'request', props.process.name)}
            />

            <TcohV2RequestSummary {...props} />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                {patient &&
                    patient.gender! !== Gender.male &&
                    patient.gender! !== Gender.female &&
                    props.request.customControlData.geslachtHolter && (
                        <HorizontalFormGroupRead>
                            <FormLabel>Geslacht</FormLabel>
                            <InputContainer>
                                <FormParagraph>
                                    {props.request.customControlData.geslachtHolter === Gender.male
                                        ? translateKey('genderEnum.male')
                                        : translateKey('genderEnum.female')}
                                </FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>
                    )}
                {props.request.customControlData.hasPacemaker && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Pacemaker</FormLabel>
                        <InputContainer>
                            <FormParagraph>{props.request.customControlData.hasPacemaker}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}
                <IndicatieRead customControlData={props.request.customControlData} />
                <MedicatieRead customControlData={props.request.customControlData} />
                {props.request.customControlData.bloeddruk_systolisch && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Bloeddruk systolisch</FormLabel>
                        <InputContainer>
                            <FormParagraph>
                                {props.request.customControlData.bloeddruk_systolisch + ' mmHg'}
                            </FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}
                {props.request.customControlData.bloeddruk_diastolisch && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Bloeddruk diastolisch</FormLabel>
                        <InputContainer>
                            <FormParagraph>
                                {props.request.customControlData.bloeddruk_diastolisch + ' mmHg'}
                            </FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}
                {props.request.customControlData.toelichting && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Toelichting</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{props.request.customControlData.toelichting}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}
                {props.request.customControlData.lengte !== null && props.request.customControlData.gewicht !== null ? (
                    <HorizontalFormGroupRead>
                        <FormLabel>BMI</FormLabel>
                        <InputContainer>
                            <FormParagraph>
                                {calculateBmi(
                                    props.request.customControlData.gewicht,
                                    props.request.customControlData.lengte
                                )}
                            </FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                ) : null}
                <AdviesBijAfwijkendeBeoordelingRead {...props.request.customControlData} />
                {props.request.customControlData?.contactgegevens_patient && (
                    <>
                        <PatientContactRead {...props} />
                    </>
                )}
                {props.request.customControlData.isServiceOrganizationSendingHomeHolter && (
                    <>
                        <FormDivider />
                        <HeaderDivider text="Verzendadres patiënt" />
                        <HorizontalFormGroupRead>
                            <FormLabel>Adres</FormLabel>
                            <InputContainer>
                                <FormParagraph>{getAddress(props.request.customControlData)}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>
                    </>
                )}
                <TcohFollowingSteps holterType={getHolterType()} />
            </CustomControlCollapseBody>
            <ProcessItemFooter
                isItemOpen={props.itemState.isOpen}
                labelLess={translateKey('request.hideRequest')}
                labelMore={translateKey('request.showRequest')}
                onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
            />
            <RequestReadButtons {...props} />
            <HideRequestedExaminationInTaskListAndHomeCheckbox
                isLastItem={props.isLastItem}
                reload={props.reloadRequest}
                request={props.request}
            />
        </ProcessContainer>
    );

    async function loadPatient() {
        const url = `/api/patients/${props.process.patientId}`;
        const result = await getJson(url);
        setPatient(result.json);
    }

    function getHolterType(): HolterProcedureType {
        if (props.request.customControlData.isServiceOrganizationSendingHomeHolter) {
            return HolterProcedureType.Home;
        }

        return HolterProcedureType.External;
    }

    function getAddress(customControlData: TcohRequestFinalValidated) {
        let address: IAddress = {};
        address = { ...customControlData.afleveradres_patient };

        return (
            <>
                {address.street} {address.houseNumber} {address.houseNumberSuffix} <br />
                {address.zip} <br />
                {address.city}
            </>
        );
    }
}
